import { useState } from "react";
import { FaRedo } from "react-icons/fa";
import { IoIosSave } from "react-icons/io";
import { IoCloseSharp } from "react-icons/io5";
import styled from "styled-components";

import { useDialog } from "~/src/modules/ui-kit";

import useMapRevitValue from "./useMapRevitValue";
import useUnmapRevitValue from "./useUnmapRevitValue";
import * as utils from "./utils";
import { useParameter } from "../../../../../modules/shared-entities";
import Confirm, { StyledDialog } from "../../../components/Confirm";
import Icon from "../../../components/Icon";


const StyledIcon = styled(Icon)`
    padding: 0;
    visibility: ${ ({ onClick }) => onClick ? "visible" : "hidden" };
`;

const StyledIcons = styled.div`
    display: flex;
    flex-direction: row;
`;

const Styled = styled.div`
    align-items: center;
    display: grid;
    grid-column: 1 / span 4;
    grid-template-columns: subgrid;
    padding: 2px 0;

    &:not(:last-of-type) {
        border-bottom: 1px solid #eee;
    }

    span {
        cursor: default;
    }

    span[data-type-match] {
        color: #aaa;
        font-size: .6rem;

        &[data-type-match=false] {
            color: #c00;
        }
    }

    span[contenteditable] {
        border: 1px solid transparent;
        border-radius: 2px;
        padding: 2px 12px 2px 8px;

        &[data-placeholder]:empty::after {
            color: #aaa;
            content: attr(data-placeholder);
            font-size: .8rem;
        }

        &:hover {
            border-color: #aaa !important;
        }
    }

    &.new {
        span {
            border-color: #eee !important;
        }
    }

    &:hover {
        span[contenteditable] {
            border: 1px solid #ddd;
        }
    }

    h1 span {
        background: #eee;
        border-radius: 2px;
        font-weight: bold;
        padding: 2px 8px;
    }
`;

export default function Mapping(props) {
    const parameter = useParameter(props.parameterID);

    const [value, setValue] = useState(props.value);
    const [revitValue, setRevitValue] = useState(props.revitValue);

    const [Dialog, open, close] = useDialog({ Component: StyledDialog });

    const [resetKey, setResetKey] = useState(0);

    const [mapExecute] = useMapRevitValue(
        props.parameterID,
        props.bimClassID,
        value ?? props.value,
        revitValue,
    );
    const [unmapExecute] = useUnmapRevitValue(props.id);

    if (parameter.loading) return undefined;

    function onValueChange(event) {
        setValue(
            utils.parseValueInput(
                event.target.textContent
            )
        );
    }

    function onRevitValueChange(event) {
        setRevitValue(
            utils.parseValueInput(
                event.target.textContent
            )
        );
    }

    function onRemove() {
        void unmapExecute();
    }

    function onReset() {
        setValue(props.value);
        setRevitValue(props.revitValue);
        setResetKey((key) => key + 1);
    }

    function onSave() {
        void mapExecute();
        if (!props.id) {
            props.onEvict();
        }
    }

    const [match, _, valueType] = utils.checkDataType(parameter.data.parameter, value);
    const label = utils.getValueLabel(parameter.data?.parameter, props.value);

    return (
        <Styled className={ !props.id ? "new" : undefined }>
            <span
                data-type-match={ match }
            >
                [{ valueType }]
            </span>
            <span
                data-placeholder="property"
                key={ "a" + resetKey }
                contentEditable={ props.id === undefined && !label ? true : undefined }
                onInput={ onValueChange }
                suppressContentEditableWarning
            >
                { label ?? props.value }
            </span>
            <span
                data-placeholder="revit"
                key={ "b" + resetKey }
                contentEditable
                onInput={ onRevitValueChange }
                suppressContentEditableWarning
            >
                { props.revitValue }
            </span>
            <StyledIcons>
                <StyledIcon
                    onClick={ props.id ? open : props.onEvict }
                    title="remove"
                >
                    <IoCloseSharp style={ { fontSize: "1.2rem" } } />
                </StyledIcon>
                <StyledIcon
                    onClick={
                        props.id
                            ? (props.revitValue === revitValue ? undefined : onReset)
                            : ((value || revitValue) ? onReset : undefined)
                    }
                    title="reset"
                >
                    <FaRedo style={ { fontSize: ".7rem", padding: "0 4px" } } />
                </StyledIcon>
                <StyledIcon
                    onClick={
                        props.id
                            ? (props.revitValue === revitValue ? undefined : onSave)
                            : (value && revitValue ? onSave : undefined)
                    }
                    title="save"
                >
                    <IoIosSave style={ { fontSize: "1rem", padding: "0 2px" } } />
                </StyledIcon>
            </StyledIcons>
            <Dialog>
                <Confirm
                    no={ close }
                    yes={ onRemove }
                >
                    <h1>Delete <span>{ label ?? props.value }</span> to <span>{ props.revitValue }</span> mapping?</h1>
                </Confirm>
            </Dialog>
        </Styled>
    );
}
